export type CmtsFormOption = { readonly value: string; readonly label: string };

function labelFromCode(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function cmtsOptionsFromCodes(values: readonly string[]): readonly CmtsFormOption[] {
  return values.map((value) => ({ value, label: labelFromCode(value) }));
}

export const CMTS_PRIORITY_OPTIONS: readonly CmtsFormOption[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

export const CMTS_SEVERITY_OPTIONS: readonly CmtsFormOption[] = [
  { value: "minor", label: "Minor" },
  { value: "major", label: "Major" },
  { value: "critical", label: "Critical" },
];


export const CMTS_SOURCE_CHANNEL_OPTIONS: readonly CmtsFormOption[] = [
  { value: "internal", label: "Internal" },
  { value: "customer", label: "Customer" },
  { value: "supplier", label: "Supplier" },
  { value: "system", label: "System / ERP" },
];

export const CMTS_CONFIDENTIALITY_OPTIONS: readonly CmtsFormOption[] = [
  { value: "internal", label: "Internal" },
  { value: "restricted", label: "Restricted" },
  { value: "customer-visible", label: "Customer visible" },
  { value: "supplier-visible", label: "Supplier visible" },
];

export const CMTS_CALENDAR_EVENT_TYPE_OPTIONS = cmtsOptionsFromCodes([
  "ACKNOWLEDGEMENT_DUE",
  "CONTAINMENT_DUE",
  "RCA_DUE",
  "CAPA_DUE",
  "VERIFICATION_DUE",
  "CLOSURE_DUE",
  "CUSTOMER_RESPONSE_DUE",
  "SUPPLIER_RESPONSE_DUE",
  "ESCALATION_REVIEW_DUE",
  "AUDIT_REVIEW_DUE",
]);

export const CMTS_CAPA_ACTION_TYPE_OPTIONS: readonly CmtsFormOption[] = [
  { value: "Correction", label: "Correction" },
  { value: "Corrective Action", label: "Corrective Action" },
  { value: "Preventive Action", label: "Preventive Action" },
];

export const CMTS_CONFIGURATION_TYPE_OPTIONS: readonly CmtsFormOption[] = [
  { value: "workflow", label: "Workflow" },
  { value: "sla", label: "SLA" },
  { value: "routing", label: "Routing" },
  { value: "reference", label: "Reference Data" },
  { value: "sod", label: "SoD" },
];

export const CMTS_ACTIVATION_MODE_OPTIONS: readonly CmtsFormOption[] = [
  { value: "Manual", label: "Manual" },
  { value: "Scheduled", label: "Scheduled" },
];

export const CMTS_ESCALATION_TYPE_OPTIONS: readonly CmtsFormOption[] = [
  { value: "SLA Breach", label: "SLA Breach" },
  { value: "Critical Complaint", label: "Critical Complaint" },
  { value: "Repeat Issue", label: "Repeat Issue" },
  { value: "Management Review", label: "Management Review" },
];

export const CMTS_EVIDENCE_FILE_TYPE_OPTIONS: readonly CmtsFormOption[] = [
  { value: "pdf", label: "PDF" },
  { value: "image", label: "Image" },
  { value: "xlsx", label: "Excel" },
  { value: "txt", label: "Text" },
];

export const CMTS_EVIDENCE_LINKED_ENTITY_OPTIONS: readonly CmtsFormOption[] = [
  { value: "COMPLAINT", label: "Complaint" },
  { value: "RCA", label: "RCA" },
  { value: "CAPA", label: "CAPA" },
  { value: "VERIFICATION", label: "Verification" },
];

export const CMTS_MESSAGE_VISIBILITY_OPTIONS: readonly CmtsFormOption[] = [
  { value: "internal", label: "Internal" },
  { value: "customer", label: "Customer visible" },
  { value: "supplier", label: "Supplier visible" },
];

export const CMTS_RCA_METHOD_OPTIONS: readonly CmtsFormOption[] = [
  { value: "5WHY", label: "5 Why" },
  { value: "FISHBONE", label: "Fishbone" },
  { value: "8D", label: "8D" },
];

export const CMTS_SLA_TYPE_OPTIONS: readonly CmtsFormOption[] = [
  { value: "Acknowledgement SLA", label: "Acknowledgement SLA" },
  { value: "Containment SLA", label: "Containment SLA" },
  { value: "RCA SLA", label: "RCA SLA" },
  { value: "CAPA SLA", label: "CAPA SLA" },
  { value: "Closure SLA", label: "Closure SLA" },
];

export const CMTS_VERIFICATION_DECISION_OPTIONS: readonly CmtsFormOption[] = [
  { value: "APPROVE", label: "Approve / Effective" },
  { value: "REJECT", label: "Reject / Not effective" },
  { value: "REWORK", label: "Request rework" },
];

export const cmtsCanonicalFormOptions = {
  priority: CMTS_PRIORITY_OPTIONS,
  severity: CMTS_SEVERITY_OPTIONS,
  calendarEventType: CMTS_CALENDAR_EVENT_TYPE_OPTIONS,
  capaActionType: CMTS_CAPA_ACTION_TYPE_OPTIONS,
  configurationType: CMTS_CONFIGURATION_TYPE_OPTIONS,
  activationMode: CMTS_ACTIVATION_MODE_OPTIONS,
  escalationType: CMTS_ESCALATION_TYPE_OPTIONS,
  evidenceFileType: CMTS_EVIDENCE_FILE_TYPE_OPTIONS,
  evidenceLinkedEntity: CMTS_EVIDENCE_LINKED_ENTITY_OPTIONS,
  messageVisibility: CMTS_MESSAGE_VISIBILITY_OPTIONS,
  rcaMethod: CMTS_RCA_METHOD_OPTIONS,
  slaType: CMTS_SLA_TYPE_OPTIONS,
  verificationDecision: CMTS_VERIFICATION_DECISION_OPTIONS,
  sourceChannel: CMTS_SOURCE_CHANNEL_OPTIONS,
  confidentiality: CMTS_CONFIDENTIALITY_OPTIONS,
  context: cmtsOptionsFromCodes(["workspace", "workflow", "report", "dashboard", "admin", "sync"]),
} as const;
